import { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { messagesApi } from '../../api/messages'
import { mediaApi } from '../../api/media'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import { useUiStore } from '../../stores/uiStore'
import { useScheduledStore } from '../../stores/scheduledStore'
import { groupsApi } from '../../api/groups'
import { botsApi, type BotCommand, type InlineQueryResult } from '../../api/bots'
import { usersApi } from '../../api/users'
import type { User } from '../../types'
import { wsClient } from '../../ws/client'
import { MediaPickerPanel } from './MediaPickerPanel'
import { RecordButton } from './RecordButton'
import { compressToJpeg, isCompressibleImage } from '../../utils/mediaCompression'

interface Props { chatId: string }

export function MessageInput({ chatId }: Props) {
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)
  // pendingMediaIds: already-uploaded IDs (GIFs etc.), pendingFiles: staged but not yet uploaded
  const [pendingMediaIds, setPendingMediaIds] = useState<string[]>([])
  const [pendingPreviews, setPendingPreviews] = useState<{ id: string; name: string; preview?: string; type: string }[]>([])
  const [pendingFiles, setPendingFiles] = useState<{ file: File; preview?: string }[]>([])
  const [noCompress, setNoCompress] = useState(false)
  const [showPreviewMenu, setShowPreviewMenu] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [ttl, setTtl] = useState<number>(0) // 0 = off
  const [showTtlMenu, setShowTtlMenu] = useState(false)
  const [showSendLaterPopup, setShowSendLaterPopup] = useState(false)
  const [showSendLaterPicker, setShowSendLaterPicker] = useState(false)
  const [sendLaterDate, setSendLaterDate] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textRef = useRef(text)

  const { appendMessage, updateMessage } = useChatStore()
  const { replyTo, clearReplyTo, editMessage, clearEditMessage, addToast, sendOnEnter } = useUiStore()
  const allScheduled = useScheduledStore((s) => s.messages)
  // filter в useMemo, а не в селекторе — иначе filter создаёт новый массив при каждом
  // вызове → useSyncExternalStore всегда видит изменение → лишние ре-рендеры
  const scheduledForChat = useMemo(
    () => allScheduled.filter((m) => m.chatId === chatId),
    [allScheduled, chatId],
  )
  const removeScheduled = useScheduledStore((s) => s.remove)
  const activeChat = useChatStore((s) => s.chats.find((c) => c.id === chatId))
  const [slowModeSecs, setSlowModeSecs] = useState(0)
  const [slowCooldownLeft, setSlowCooldownLeft] = useState(0)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionMembers, setMentionMembers] = useState<{ id: string; name: string; username?: string }[]>([])
  const [mentionIdx, setMentionIdx] = useState(0)
  const [botCommands, setBotCommands] = useState<BotCommand[]>([])
  const [cmdQuery, setCmdQuery] = useState<string | null>(null)
  const [cmdIdx, setCmdIdx] = useState(0)
  const [disablePreview, setDisablePreview] = useState(false)
  const [showContactPicker, setShowContactPicker] = useState(false)
  const [contactSearch, setContactSearch] = useState('')
  const [contactResults, setContactResults] = useState<User[]>([])
  const [sendingLocation, setSendingLocation] = useState(false)
  const [inlineBot, setInlineBot] = useState<{ botId: string; username: string } | null>(null)
  const [inlineResults, setInlineResults] = useState<InlineQueryResult[]>([])
  const [inlineLoading, setInlineLoading] = useState(false)
  const [inlineIdx, setInlineIdx] = useState(0)
  const inlineDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sendLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSendLongPress = useRef(false)

  // Keep textRef in sync for cleanup usage
  useEffect(() => { textRef.current = text }, [text])

  // Draft: load on chatId mount, save on unmount
  useEffect(() => {
    const draft = localStorage.getItem(`ggram-draft-${chatId}`) ?? ''
    setText(draft)
    return () => {
      const current = textRef.current.trim()
      if (current) {
        localStorage.setItem(`ggram-draft-${chatId}`, textRef.current)
      } else {
        localStorage.removeItem(`ggram-draft-${chatId}`)
      }
    }
  }, [chatId])

  // Bot commands: load when chatting with a bot
  useEffect(() => {
    const peerId = activeChat?.peer?.id
    if (!peerId || !activeChat?.peer?.is_bot) { setBotCommands([]); return }
    botsApi.getCommands(peerId)
      .then((r) => setBotCommands(r.commands ?? []))
      .catch(() => setBotCommands([]))
  }, [chatId, activeChat?.peer?.id, activeChat?.peer?.is_bot])

  // Slow mode: fetch secs for group chats
  useEffect(() => {
    if (activeChat?.type !== 'group') { setSlowModeSecs(0); return }
    groupsApi.get(chatId)
      .then((g) => setSlowModeSecs(g.slow_mode_secs ?? 0))
      .catch(() => {})
  }, [chatId, activeChat?.type])

  // Slow mode: countdown from localStorage
  useEffect(() => {
    if (!slowModeSecs) return
    const key = `slow_mode_last_${chatId}`
    const tick = () => {
      const last = Number(localStorage.getItem(key) ?? 0)
      const remaining = Math.max(0, Math.ceil((last + slowModeSecs * 1000 - Date.now()) / 1000))
      setSlowCooldownLeft(remaining)
    }
    tick()
    const t = setInterval(tick, 500)
    return () => clearInterval(t)
  }, [chatId, slowModeSecs])

  // Contact picker search
  useEffect(() => {
    if (!showContactPicker) return
    if (!contactSearch.trim()) { setContactResults([]); return }
    usersApi.search(contactSearch.trim())
      .then((r) => setContactResults(r.users ?? []))
      .catch(() => {})
  }, [contactSearch, showContactPicker])

  const sendContact = async (user: User) => {
    setShowContactPicker(false)
    setContactSearch('')
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ')
    const phone = (user as User & { phone?: string }).phone ?? ''
    const lines = [`👤 **${name}**`]
    if (user.username) lines.push(`@${user.username}`)
    if (phone) lines.push(`📞 ${phone}`)
    const contactText = lines.join('\n')
    try {
      const msg = await messagesApi.send(chatId, { text: contactText })
      appendMessage(msg)
    } catch {
      addToast('error', 'Не удалось отправить контакт')
    }
  }

  const sendLocation = () => {
    if (!navigator.geolocation) { addToast('error', 'Геолокация не поддерживается'); return }
    setSendingLocation(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setSendingLocation(false)
        const { latitude: lat, longitude: lng } = pos.coords
        const mapsUrl = `https://maps.google.com/?q=${lat},${lng}`
        const locationText = `📍 Location\n${lat.toFixed(6)}, ${lng.toFixed(6)}\n${mapsUrl}`
        try {
          const msg = await messagesApi.send(chatId, { text: locationText })
          appendMessage(msg)
        } catch {
          addToast('error', 'Не удалось отправить геолокацию')
        }
      },
      () => { setSendingLocation(false); addToast('error', 'Доступ к геолокации запрещён') },
      { timeout: 10000 }
    )
  }

  // When entering edit mode, pre-fill the textarea
  useEffect(() => {
    if (editMessage && editMessage.chatId === chatId) {
      setText(editMessage.text)
      textareaRef.current?.focus()
    }
  }, [editMessage, chatId])

  const sendTyping = useCallback(() => {
    wsClient.sendTypingStart(chatId)
    if (typingTimer.current) clearTimeout(typingTimer.current)
    typingTimer.current = setTimeout(() => wsClient.sendTypingStop(chatId), 5000)
  }, [chatId])

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setText(val)
    autoResize(e.target)
    if (val) sendTyping()
    else wsClient.sendTypingStop(chatId)

    // /commands for bot chats
    if (botCommands.length > 0) {
      const cmdMatch = val.match(/^\/(\w*)$/)
      if (cmdMatch) {
        setCmdQuery(cmdMatch[1].toLowerCase())
        setCmdIdx(0)
      } else {
        setCmdQuery(null)
      }
    } else {
      setCmdQuery(null)
    }

    // @bot inline query — detect "@botname <query>" pattern at start of message
    const inlineMatch = val.match(/^@(\w+) (.*)$/)
    if (inlineMatch) {
      const botUsername = inlineMatch[1]
      const query = inlineMatch[2]
      if (inlineDebounce.current) clearTimeout(inlineDebounce.current)
      inlineDebounce.current = setTimeout(async () => {
        // Resolve bot by username if not already cached
        if (!inlineBot || inlineBot.username !== botUsername) {
          try {
            const res = await usersApi.search(botUsername)
            const user = (res.users ?? []).find((u) => u.username === botUsername && u.is_bot)
            if (!user) { setInlineBot(null); setInlineResults([]); return }
            setInlineBot({ botId: user.id, username: botUsername })
            setInlineLoading(true)
            const r = await botsApi.inlineQuery(user.id, query, activeChat?.type ?? '')
            setInlineResults(r.results ?? [])
            setInlineIdx(0)
          } catch { setInlineResults([]) }
          finally { setInlineLoading(false) }
        } else {
          setInlineLoading(true)
          try {
            const r = await botsApi.inlineQuery(inlineBot.botId, query, activeChat?.type ?? '')
            setInlineResults(r.results ?? [])
            setInlineIdx(0)
          } catch { setInlineResults([]) }
          finally { setInlineLoading(false) }
        }
      }, 400)
    } else {
      if (inlineDebounce.current) clearTimeout(inlineDebounce.current)
      if (inlineBot) { setInlineBot(null); setInlineResults([]) }
    }

    // @mentions for group chats
    if (activeChat?.type === 'group') {
      const cursor = e.target.selectionStart
      const textBefore = val.slice(0, cursor)
      const mentionMatch = textBefore.match(/@(\w*)$/)
      if (mentionMatch) {
        const q = mentionMatch[1].toLowerCase()
        setMentionQuery(q)
        setMentionIdx(0)
        if (!mentionMembers.length || q === '') {
          groupsApi.listMembers(chatId).then((res) => {
            const mems = (res.members ?? []).map((m) => ({
              id: m.user_id,
              name: [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || m.user_id,
              username: m.user?.username,
            }))
            setMentionMembers(mems)
          }).catch(() => {})
        }
      } else {
        setMentionQuery(null)
      }
    }
  }

  const filteredMentions = mentionQuery !== null
    ? mentionMembers.filter((m) =>
        m.name.toLowerCase().includes(mentionQuery) ||
        (m.username?.toLowerCase().includes(mentionQuery) ?? false)
      ).slice(0, 5)
    : []

  const filteredCommands = cmdQuery !== null
    ? botCommands.filter((c) => c.command.startsWith(cmdQuery)).slice(0, 8)
    : []

  const insertCommand = (cmd: BotCommand) => {
    setText(`/${cmd.command} `)
    setCmdQuery(null)
    textareaRef.current?.focus()
  }

  const insertMention = (member: { id: string; name: string; username?: string }) => {
    const el = textareaRef.current
    if (!el) return
    const cursor = el.selectionStart
    const textBefore = text.slice(0, cursor)
    const mentionStart = textBefore.lastIndexOf('@')
    const handle = member.username ?? member.name.replace(/\s/g, '')
    const newText = text.slice(0, mentionStart) + `@${handle} ` + text.slice(cursor)
    setText(newText)
    setMentionQuery(null)
    requestAnimationFrame(() => {
      const newCursor = mentionStart + handle.length + 2
      el.setSelectionRange(newCursor, newCursor)
      el.focus()
    })
  }

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'
  }

  // Wrap selected text with formatting markers
  const wrapSelection = (open: string, close = open) => {
    const el = textareaRef.current
    if (!el) return
    const { selectionStart: s, selectionEnd: e, value } = el
    const selected = value.slice(s, e)
    const newText = value.slice(0, s) + open + selected + close + value.slice(e)
    setText(newText)
    requestAnimationFrame(() => {
      el.setSelectionRange(s + open.length, e + open.length)
      el.focus()
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Navigate command autocomplete
    if (filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCmdIdx((i) => Math.min(i + 1, filteredCommands.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCmdIdx((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Tab' || (e.key === 'Enter' && filteredCommands[cmdIdx])) {
        e.preventDefault()
        insertCommand(filteredCommands[cmdIdx])
        return
      }
      if (e.key === 'Escape') { setCmdQuery(null); return }
    }

    // Navigate mention autocomplete
    if (filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => Math.min(i + 1, filteredMentions.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Tab' || (e.key === 'Enter' && filteredMentions[mentionIdx])) {
        e.preventDefault()
        insertMention(filteredMentions[mentionIdx])
        return
      }
      if (e.key === 'Escape') { setMentionQuery(null); return }
    }

    if (sendOnEnter) {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        send()
        return
      }
    } else {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        send()
        return
      }
    }
    if (e.key === 'Enter' && e.shiftKey && !e.ctrlKey) {
      e.preventDefault()
      send(true)
      return
    }
    if (e.key === 'ArrowUp' && !text.trim()) {
      e.preventDefault()
      const { messages } = useChatStore.getState()
      const chatMessages = messages[chatId] ?? []
      const { userId } = useAuthStore.getState()
      const lastMine = [...chatMessages].reverse().find(
        (m) => m.sender_id === userId && m.text && !m.deleted_for_all,
      )
      if (lastMine) {
        useUiStore.getState().setEditMessage({ chatId, messageId: lastMine.id, text: lastMine.text })
      }
      return
    }
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') { e.preventDefault(); wrapSelection('**') }
      else if (e.key === 'i') { e.preventDefault(); wrapSelection('_') }
      else if (e.key === 'u') { e.preventDefault(); wrapSelection('__') }
      else if (e.key === 'e') { e.preventDefault(); wrapSelection('`') }
      else if (e.shiftKey && e.key === 'X') { e.preventDefault(); wrapSelection('~~') }
      else if (e.shiftKey && e.key === 'P') { e.preventDefault(); wrapSelection('||') }
    }
  }

  const sendGif = async (gifUrl: string) => {
    setUploading(true)
    try {
      const res = await fetch(gifUrl)
      const blob = await res.blob()
      const file = new File([blob], `gif_${Date.now()}.gif`, { type: 'image/gif' })
      const uploaded = await mediaApi.upload(file)
      const msg = await messagesApi.send(chatId, { text: '', media_ids: [uploaded.id] })
      appendMessage(msg)
    } catch {
      addToast('error', 'Failed to send GIF')
    } finally {
      setUploading(false)
    }
  }

  const sendSticker = async (fileId: string, emoji: string) => {
    try {
      const msg = await messagesApi.send(chatId, { text: emoji, media_ids: [fileId] })
      appendMessage(msg)
    } catch {
      addToast('error', 'Не удалось отправить стикер')
    }
  }

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current
    if (!el) return setText((t) => t + emoji)
    const pos = el.selectionStart
    setText((t) => t.slice(0, pos) + emoji + t.slice(pos))
    requestAnimationFrame(() => {
      el.setSelectionRange(pos + emoji.length, pos + emoji.length)
      el.focus()
    })
  }

  const send = async (silent = false) => {
    const trimmed = text.trim()
    if ((!trimmed && !pendingMediaIds.length && !pendingFiles.length) || sending || slowCooldownLeft > 0) return

    setSending(true)
    wsClient.sendTypingStop(chatId)
    if (typingTimer.current) clearTimeout(typingTimer.current)

    try {
      // Edit mode
      if (editMessage && editMessage.chatId === chatId) {
        const updated = await messagesApi.edit(chatId, editMessage.messageId, trimmed)
        updateMessage(chatId, editMessage.messageId, { text: updated.text, edited_at: updated.edited_at })
        clearEditMessage()
        setText('')
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
        return
      }

      // Upload staged files now (with compression unless noCompress)
      const stagedFiles = [...pendingFiles]
      const uploadedFileIds: string[] = []
      for (const { file } of stagedFiles) {
        const isHeic = file.type.includes('heic') || file.type.includes('heif')
        const toUpload = (!noCompress && isCompressibleImage(file.type))
          ? await compressToJpeg(file)
          : file
        // noConvert=true when user chose "без сжатия" for a HEIC file —
        // backend must skip HEIC→JPEG conversion so it stays as a downloadable file
        const uploaded = await mediaApi.upload(toUpload, noCompress && isHeic)
        uploadedFileIds.push(uploaded.id)
      }

      // Combine staged uploads with already-uploaded IDs (GIFs etc.)
      const mediaIdsCopy = [...uploadedFileIds, ...pendingMediaIds]
      const replyToId = replyTo?.messageId

      // Optimistic: show message immediately with pending state
      const tempId = `pending_${Date.now()}`
      const { userId: myId } = useAuthStore.getState()
      const optimistic = {
        id: tempId,
        chat_id: chatId,
        sender_id: myId ?? '',
        text: trimmed,
        media_ids: mediaIdsCopy.length ? mediaIdsCopy : null,
        reply_to_message_id: replyToId ?? '',
        forward_from_chat_id: '',
        forward_from_msg_id: '',
        deleted_for_all: false,
        ttl_seconds: ttl > 0 ? ttl : 0,
        created_at: new Date().toISOString(),
        edited_at: null,
        pending: true,
      }
      appendMessage(optimistic)
      setText('')
      localStorage.removeItem(`ggram-draft-${chatId}`)
      setPendingMediaIds([])
      setPendingPreviews((prev) => { prev.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview) }); return [] })
      setPendingFiles((prev) => { prev.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview) }); return [] })
      setNoCompress(false)
      clearReplyTo()
      if (textareaRef.current) textareaRef.current.style.height = 'auto'

      const msg = await messagesApi.send(chatId, {
        text: trimmed,
        media_ids: mediaIdsCopy.length ? mediaIdsCopy : undefined,
        reply_to_message_id: replyToId,
        ttl_seconds: ttl > 0 ? ttl : undefined,
        silent: silent || undefined,
      })
      // Replace optimistic message with confirmed one
      useChatStore.getState().removeMessage(chatId, tempId)
      appendMessage(msg)
      if (slowModeSecs > 0) {
        localStorage.setItem(`slow_mode_last_${chatId}`, String(Date.now()))
        setSlowCooldownLeft(slowModeSecs)
      }
    } catch {
      addToast('error', editMessage ? 'Не удалось изменить сообщение' : 'Не удалось отправить сообщение')
    } finally {
      setSending(false)
      textareaRef.current?.focus()
    }
  }

  const scheduleMessage = async () => {
    const trimmed = text.trim()
    if (!trimmed && !pendingMediaIds.length && !pendingFiles.length) return

    // Upload staged files immediately for scheduled messages
    const stagedFiles = [...pendingFiles]
    const uploadedFileIds: string[] = []
    if (stagedFiles.length > 0) {
      setUploading(true)
      try {
        for (const { file } of stagedFiles) {
          const isHeic = file.type.includes('heic') || file.type.includes('heif')
          const toUpload = (!noCompress && isCompressibleImage(file.type)) ? await compressToJpeg(file) : file
          const uploaded = await mediaApi.upload(toUpload, noCompress && isHeic)
          uploadedFileIds.push(uploaded.id)
        }
      } catch {
        addToast('error', 'Не удалось загрузить файл')
        setUploading(false)
        return
      }
      setUploading(false)
    }

    const allMediaIds = [...uploadedFileIds, ...pendingMediaIds]
    useScheduledStore.getState().add({
      chatId,
      text: trimmed,
      mediaIds: allMediaIds.length ? allMediaIds : undefined,
      replyToId: replyTo?.messageId,
      scheduledFor: new Date(sendLaterDate).toISOString(),
    })
    setText('')
    setPendingMediaIds([])
    setPendingPreviews((prev) => { prev.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview) }); return [] })
    setPendingFiles((prev) => { prev.forEach((p) => { if (p.preview) URL.revokeObjectURL(p.preview) }); return [] })
    setNoCompress(false)
    clearReplyTo()
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    setShowSendLaterPopup(false)
    setShowSendLaterPicker(false)
    setSendLaterDate('')
    addToast('success', `Запланировано на ${new Date(sendLaterDate).toLocaleString()}`)
  }

  const handleSendPointerDown = () => {
    isSendLongPress.current = false
    sendLongPressTimer.current = setTimeout(() => {
      isSendLongPress.current = true
      setShowSendLaterPopup(true)
    }, 400)
  }

  const handleSendPointerUp = () => {
    if (sendLongPressTimer.current) clearTimeout(sendLongPressTimer.current)
    // Click fires separately; only send if it was NOT a long press
    if (!isSendLongPress.current) send()
    isSendLongPress.current = false
  }

  const handleSendPointerCancel = () => {
    if (sendLongPressTimer.current) clearTimeout(sendLongPressTimer.current)
    isSendLongPress.current = false
  }

  // Stage a file for sending (no upload yet — happens at send time).
  const stageFile = (rawFile: File) => {
    if (rawFile.size > 2 * 1024 * 1024 * 1024) {
      addToast('error', 'Файл слишком большой (макс. 2 ГБ)')
      return
    }

    // Fix missing MIME type (common on iOS for HEIC/MOV)
    let file = rawFile
    if (!file.type || file.type === 'application/octet-stream') {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      const extMime: Record<string, string> = {
        heic: 'image/heic', heif: 'image/heif',
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
        mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', mkv: 'video/x-matroska',
        avi: 'video/x-msvideo', '3gp': 'video/3gpp',
        mp3: 'audio/mpeg', m4a: 'audio/mp4', ogg: 'audio/ogg', wav: 'audio/wav',
      }
      if (extMime[ext]) file = new File([rawFile], rawFile.name, { type: extMime[ext] })
    }

    // Local preview (no upload)
    let preview: string | undefined
    if (file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif')) {
      preview = URL.createObjectURL(file)
    } else if (file.type.startsWith('video/')) {
      preview = URL.createObjectURL(file)
    }

    setPendingFiles((prev) => [...prev, { file, preview }])
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    files.forEach(stageFile)
  }

  // Paste from clipboard
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items)
    const imageItem = items.find((item) => item.type.startsWith('image/'))
    if (imageItem) {
      e.preventDefault()
      const file = imageItem.getAsFile()
      if (!file) return
      stageFile(new File([file], `paste_${Date.now()}.png`, { type: file.type }))
    }
  }

  const TTL_OPTIONS = [
    { label: 'Off', value: 0 },
    { label: '10s', value: 10 },
    { label: '1 min', value: 60 },
    { label: '1 hour', value: 3600 },
    { label: '1 day', value: 86400 },
    { label: '1 week', value: 604800 },
  ]


  return (
    <div
      className={`border-t border-divider bg-bg-primary relative ${dragOver ? 'ring-2 ring-accent ring-inset' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        Array.from(e.dataTransfer.files).slice(0, 5).forEach(stageFile)
      }}
    >
      {dragOver && (
        <div className="absolute inset-0 bg-accent/10 border-2 border-dashed border-accent rounded-t-xl z-10
                        flex items-center justify-center pointer-events-none">
          <p className="text-accent font-medium">Drop files to upload</p>
        </div>
      )}

      {/* /command autocomplete */}
      {filteredCommands.length > 0 && (
        <div className="border-b border-divider bg-bg-primary max-h-48 overflow-y-auto">
          {filteredCommands.map((cmd, idx) => (
            <button
              key={cmd.command}
              onMouseDown={(e) => { e.preventDefault(); insertCommand(cmd) }}
              className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors
                ${idx === cmdIdx ? 'bg-accent/10' : 'hover:bg-bg-hover'}`}
            >
              <span className={`text-sm font-mono font-medium ${idx === cmdIdx ? 'text-accent' : 'text-txt-primary'}`}>
                /{cmd.command}
              </span>
              {cmd.description && (
                <span className="text-txt-meta text-xs truncate">{cmd.description}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* @mention autocomplete */}
      {filteredMentions.length > 0 && (
        <div className="border-b border-divider bg-bg-primary">
          {filteredMentions.map((m, idx) => (
            <button
              key={m.id}
              onMouseDown={(e) => { e.preventDefault(); insertMention(m) }}
              className={`w-full text-left px-4 py-2 flex items-center gap-2 transition-colors
                ${idx === mentionIdx ? 'bg-accent/10 text-accent' : 'hover:bg-bg-hover text-txt-primary'}`}
            >
              <span className="text-sm font-medium">{m.name}</span>
              {m.username && <span className="text-txt-meta text-xs">@{m.username}</span>}
            </button>
          ))}
        </div>
      )}

      {/* @bot inline query results */}
      {inlineBot && (inlineResults.length > 0 || inlineLoading) && (
        <div className="border-b border-divider bg-bg-primary max-h-56 overflow-y-auto">
          {inlineLoading && inlineResults.length === 0 && (
            <div className="px-4 py-3 text-txt-meta text-sm text-center">Поиск...</div>
          )}
          {inlineResults.map((res, idx) => (
            <button
              key={res.id}
              onMouseDown={(e) => {
                e.preventDefault()
                const messageText = res.input_message_content?.message_text ?? res.title ?? res.description ?? ''
                if (messageText) {
                  messagesApi.send(chatId, { text: messageText }).then(appendMessage).catch(() => addToast('error', 'Не удалось отправить'))
                }
                setText('')
                setInlineBot(null)
                setInlineResults([])
              }}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors
                ${idx === inlineIdx ? 'bg-accent/10' : 'hover:bg-bg-hover'}`}
            >
              {res.thumb_url && (
                <img src={res.thumb_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
              )}
              {res.photo_url && !res.thumb_url && (
                <img src={res.photo_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
              )}
              {!res.thumb_url && !res.photo_url && (
                <div className="w-10 h-10 rounded bg-bg-hover flex items-center justify-center text-txt-meta text-xs shrink-0">
                  {res.type === 'article' ? '📄' : res.type === 'photo' ? '🖼' : res.type === 'gif' ? '🎞' : '•'}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-txt-primary text-sm font-medium truncate">{res.title}</p>
                {res.description && <p className="text-txt-meta text-xs truncate">{res.description}</p>}
              </div>
            </button>
          ))}
        </div>
      )}


      {/* Edit preview */}
      {editMessage && editMessage.chatId === chatId && (
        <div className="flex items-center gap-3 px-3 py-2 border-b border-divider bg-bg-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent shrink-0">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <div className="border-l-2 border-accent pl-2 flex-1 min-w-0">
            <p className="text-accent text-xs font-medium">Изменить сообщение</p>
            <p className="text-txt-primary text-sm truncate">{editMessage.text}</p>
          </div>
          <button onClick={() => { clearEditMessage(); setText('') }} className="w-7 h-7 flex items-center justify-center rounded-full text-txt-meta hover:text-txt-primary hover:bg-bg-hover transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}
      {/* Reply preview */}
      {replyTo && !editMessage && (
        <div className="flex items-center gap-3 px-3 py-2 border-b border-divider bg-bg-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent shrink-0">
            <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
          </svg>
          <div className="border-l-2 border-accent pl-2 flex-1 min-w-0">
            <p className="text-accent text-xs font-medium">Ответить</p>
            <p className="text-txt-primary text-sm truncate">{replyTo.text || '📎 Вложение'}</p>
          </div>
          <button onClick={clearReplyTo} className="w-7 h-7 flex items-center justify-center rounded-full text-txt-meta hover:text-txt-primary hover:bg-bg-hover transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Pending media previews */}
      {(pendingFiles.length > 0 || pendingMediaIds.length > 0) && (
        <div className="flex items-start gap-2 px-4 py-2 border-b border-divider">
          <div className="flex gap-2 flex-wrap flex-1">
            {/* Staged files (not yet uploaded) */}
            {pendingFiles.map(({ file, preview }, i) => (
              <div key={i} className="relative group">
                {preview && file.type.startsWith('video/') ? (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-bg-input">
                    <video src={preview} className="w-full h-full object-cover" muted playsInline />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" className="drop-shadow">
                        <polygon points="5,3 19,12 5,21"/>
                      </svg>
                    </div>
                  </div>
                ) : preview ? (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-bg-input">
                    <img src={preview} alt="preview" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-bg-input rounded-xl px-3 py-2 max-w-[140px]">
                    <span className="text-lg shrink-0">📎</span>
                    <span className="text-txt-secondary text-xs truncate">{file.name}</span>
                  </div>
                )}
                <button
                  onClick={() => {
                    setPendingFiles((prev) => {
                      const item = prev[i]
                      if (item?.preview) URL.revokeObjectURL(item.preview)
                      return prev.filter((_, j) => j !== i)
                    })
                  }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-txt-primary text-bg-primary text-[10px]
                             flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >✕</button>
              </div>
            ))}

            {/* Already-uploaded media (GIFs etc.) */}
          {pendingMediaIds.map((id) => {
            const meta = pendingPreviews.find((p) => p.id === id)
            return (
              <div key={id} className="relative group">
                {meta?.preview && meta.type.startsWith('video/') ? (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-bg-input">
                    <video src={meta.preview} className="w-full h-full object-cover" muted playsInline />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" className="drop-shadow">
                        <polygon points="5,3 19,12 5,21"/>
                      </svg>
                    </div>
                  </div>
                ) : meta?.preview ? (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-bg-input">
                    <img src={meta.preview} alt="preview" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-bg-input rounded-xl px-3 py-2 max-w-[140px]">
                    <span className="text-lg shrink-0">📎</span>
                    <span className="text-txt-secondary text-xs truncate">{meta?.name ?? 'File'}</span>
                  </div>
                )}
                <button
                  onClick={() => {
                    setPendingMediaIds((ids) => ids.filter((x) => x !== id))
                    setPendingPreviews((prev) => {
                      const item = prev.find((p) => p.id === id)
                      if (item?.preview) URL.revokeObjectURL(item.preview)
                      return prev.filter((p) => p.id !== id)
                    })
                  }}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-txt-primary text-bg-primary text-[10px]
                             flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >✕</button>
              </div>
            )
          })}
          </div>

          {/* "⋯" menu — only when there are staged image files */}
          {pendingFiles.some((f) => f.file.type.startsWith('image/')) && (
            <div className="relative shrink-0 self-center">
              {showPreviewMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowPreviewMenu(false)} />
                  <div className="absolute bottom-full right-0 mb-2 bg-bg-primary border border-divider rounded-xl shadow-xl z-50 overflow-hidden min-w-[220px] animate-fade-in">
                    <button
                      onClick={() => { setNoCompress((v) => !v); setShowPreviewMenu(false) }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-hover transition-colors"
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors
                        ${noCompress ? 'bg-accent border-accent' : 'border-txt-meta/60'}`}>
                        {noCompress && (
                          <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1.5,5 3.5,7.5 8.5,2"/>
                          </svg>
                        )}
                      </div>
                      <span className="text-txt-primary text-sm">Отправить без сжатия</span>
                    </button>
                  </div>
                </>
              )}
              <button
                onClick={() => setShowPreviewMenu((v) => !v)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors
                  ${showPreviewMenu ? 'text-accent bg-accent/10' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
                title="Параметры отправки"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                </svg>
              </button>
            </div>
          )}

          {/* Badge: без сжатия активно */}
          {noCompress && pendingFiles.length > 0 && (
            <div className="shrink-0 self-center text-[11px] text-accent font-medium px-2 py-1 bg-accent/10 rounded-lg whitespace-nowrap">
              Оригинал
            </div>
          )}
        </div>
      )}

      <>
          {/* Link preview toggle — показывается только когда в тексте есть ссылка */}
          {/https?:\/\//.test(text) && (
            <div className="flex items-center px-3 pt-1.5">
              <button
                title={disablePreview ? 'Включить превью ссылки' : 'Отключить превью ссылки'}
                onMouseDown={(e) => { e.preventDefault(); setDisablePreview((v) => !v) }}
                className={`w-7 h-7 text-xs rounded transition-colors flex items-center justify-center
                  ${disablePreview ? 'text-txt-meta line-through' : 'text-accent hover:text-blue-400'}`}
              >
                🔗
              </button>
            </div>
          )}

          {/* Input row */}
          <div className="flex items-end gap-2 px-3 py-2">
            {/* Emoji / GIF / Sticker */}
            <div className="relative">
              {showMediaPicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMediaPicker(false)} />
                  <div className="relative z-50">
                    <MediaPickerPanel
                      onEmoji={(e) => { insertEmoji(e); setShowMediaPicker(false) }}
                      onGif={(url) => { sendGif(url); setShowMediaPicker(false) }}
                      onSticker={(fid, em) => { sendSticker(fid, em); setShowMediaPicker(false) }}
                      onClose={() => setShowMediaPicker(false)}
                    />
                  </div>
                </>
              )}
              <button
                onClick={() => { setShowMediaPicker((v) => !v); setShowAttachMenu(false) }}
                className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors
                  ${showMediaPicker ? 'text-accent bg-accent/10' : 'hover:bg-bg-hover text-txt-secondary hover:text-txt-primary'}`}
                title="Emoji, GIF, Stickers"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </button>
            </div>

            {/* Paperclip: Photo/Video, Location, Contact */}
            <div className="relative">
              {showAttachMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowAttachMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-primary border border-divider rounded-2xl shadow-xl z-50 overflow-hidden animate-fade-in min-w-[170px]">
                    {/* Photo / Video */}
                    <button
                      onClick={() => { setShowAttachMenu(false); fileRef.current?.click() }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-txt-primary hover:bg-bg-hover transition-colors"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      Фото / Видео
                    </button>
                    {/* Location */}
                    <button
                      onClick={() => { setShowAttachMenu(false); sendLocation() }}
                      disabled={sendingLocation}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-txt-primary hover:bg-bg-hover transition-colors"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      Локация
                    </button>
                    {/* Contact */}
                    <button
                      onClick={() => { setShowAttachMenu(false); setShowContactPicker(true) }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-txt-primary hover:bg-bg-hover transition-colors"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                      Контакт
                    </button>
                  </div>
                </>
              )}
              <button
                onClick={() => { setShowAttachMenu((v) => !v); setShowMediaPicker(false) }}
                className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors
                  ${showAttachMenu ? 'text-accent bg-accent/10' : 'hover:bg-bg-hover text-txt-secondary hover:text-txt-primary'}`}
                title="Прикрепить"
              >
                {uploading ? (
                  <span className="animate-spin text-sm text-accent">⟳</span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                )}
              </button>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept="image/*,video/*" />
              {/* Contact picker — shown relative to paperclip button */}
              {showContactPicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowContactPicker(false)} />
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-primary border border-divider rounded-xl shadow-xl z-50 w-64 animate-fade-in">
                    <div className="p-2">
                      <input
                        autoFocus
                        type="text"
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        placeholder="Поиск пользователей..."
                        className="w-full bg-bg-input text-txt-primary rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                      />
                    </div>
                    {contactResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto pb-2">
                        {contactResults.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => sendContact(u)}
                            className="w-full text-left px-3 py-2 text-sm text-txt-primary hover:bg-bg-hover transition-colors flex items-center gap-2"
                          >
                            <span className="text-lg">👤</span>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{[u.first_name, u.last_name].filter(Boolean).join(' ')}</p>
                              {u.username && <p className="text-txt-meta text-xs">@{u.username}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {contactSearch && contactResults.length === 0 && (
                      <p className="px-3 py-3 text-txt-meta text-sm text-center">Пользователи не найдены</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Сообщение"
              rows={1}
              className="flex-1 bg-bg-input text-txt-primary rounded-2xl px-4 py-2.5 text-[15px]
                         outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta
                         resize-none overflow-hidden leading-relaxed"
            />

            {/* TTL button */}
            <div className="relative">
              <button onClick={() => setShowTtlMenu(s => !s)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors text-xs
                  ${ttl > 0 ? 'text-accent bg-accent/20' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
                title="Таймер самоуничтожения">
                ⏱
              </button>
              {showTtlMenu && (
                <div className="absolute bottom-full right-0 mb-2 bg-bg-primary border border-divider rounded-xl shadow-xl overflow-hidden z-20 min-w-[100px]">
                  {TTL_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => { setTtl(opt.value); setShowTtlMenu(false) }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-bg-hover transition-colors
                        ${ttl === opt.value ? 'text-accent font-medium' : 'text-txt-primary'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Voice or Send button */}
            {text.trim() || pendingMediaIds.length || pendingFiles.length ? (
              slowCooldownLeft > 0 ? (
                <div className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-bg-input text-txt-meta text-xs font-bold" title={`Slow mode: wait ${slowCooldownLeft}s`}>
                  {slowCooldownLeft}
                </div>
              ) : (
              <div className="relative shrink-0">
                {/* Scheduled messages count badge */}
                {scheduledForChat.length > 0 && (
                  <span className="absolute -top-1 -right-1 z-10 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center pointer-events-none leading-none">
                    {scheduledForChat.length}
                  </span>
                )}

                {/* Long-press popup */}
                {showSendLaterPopup && (
                  <>
                    <div className="fixed inset-0 z-40" onPointerDown={() => { setShowSendLaterPopup(false); setShowSendLaterPicker(false) }} />
                    <div className="absolute bottom-full right-0 mb-2 bg-bg-primary border border-divider rounded-2xl shadow-2xl z-50 w-72 animate-fade-in overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-divider">
                        <p className="text-txt-primary text-sm font-semibold">Отложенные сообщения</p>
                      </div>

                      {/* "Отправить позже" option */}
                      {!showSendLaterPicker ? (
                        <button
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setShowSendLaterPicker(true)}
                          className="flex items-center gap-3 w-full px-4 py-3 hover:bg-bg-hover transition-colors border-b border-divider"
                        >
                          <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                          </div>
                          <span className="text-txt-primary text-sm">Отправить позже</span>
                        </button>
                      ) : (
                        <div className="px-4 py-3 border-b border-divider" onPointerDown={(e) => e.stopPropagation()}>
                          <p className="text-txt-secondary text-xs mb-2">Выберите время отправки</p>
                          <input
                            type="datetime-local"
                            value={sendLaterDate}
                            onChange={(e) => setSendLaterDate(e.target.value)}
                            min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                            className="w-full bg-bg-input text-txt-primary text-sm rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-accent mb-2"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setShowSendLaterPicker(false); setSendLaterDate('') }}
                              className="flex-1 text-txt-secondary text-sm py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
                            >
                              Отмена
                            </button>
                            <button
                              disabled={!sendLaterDate}
                              onClick={scheduleMessage}
                              className="flex-1 bg-accent text-white text-sm py-1.5 rounded-lg disabled:opacity-40 hover:bg-blue-500 transition-colors"
                            >
                              Запланировать
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing scheduled messages list */}
                      {scheduledForChat.length > 0 ? (
                        <div className="max-h-52 overflow-y-auto">
                          {scheduledForChat.map((m) => (
                            <div key={m.id} onPointerDown={(e) => e.stopPropagation()} className="flex items-start gap-3 px-4 py-2.5 hover:bg-bg-hover transition-colors">
                              <div className="flex-1 min-w-0">
                                <p className="text-txt-meta text-xs mb-0.5">{new Date(m.scheduledFor).toLocaleString()}</p>
                                <p className="text-txt-primary text-sm truncate">{m.text || '📎 Медиа'}</p>
                              </div>
                              <button
                                onClick={() => removeScheduled(m.id)}
                                className="text-txt-meta hover:text-red-400 transition-colors shrink-0 mt-1 text-sm"
                                title="Отменить"
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-txt-meta text-xs text-center py-3">Нет отложенных сообщений</p>
                      )}
                    </div>
                  </>
                )}

                <button
                  onPointerDown={handleSendPointerDown}
                  onPointerUp={handleSendPointerUp}
                  onPointerLeave={handleSendPointerCancel}
                  onPointerCancel={handleSendPointerCancel}
                  disabled={sending}
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full
                             bg-accent hover:bg-blue-500 disabled:opacity-40
                             text-white transition-colors select-none touch-none"
                  title="Отправить • Удержать — отправить позже"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 pointer-events-none">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
              )
            ) : (
              <div className="flex items-center gap-1">
                {/* Badge button when there are scheduled msgs but no text */}
                {scheduledForChat.length > 0 && (
                  <div className="relative shrink-0">
                    {showSendLaterPopup && (
                      <>
                        <div className="fixed inset-0 z-40" onPointerDown={() => { setShowSendLaterPopup(false); setShowSendLaterPicker(false) }} />
                        <div className="absolute bottom-full right-0 mb-2 bg-bg-primary border border-divider rounded-2xl shadow-2xl z-50 w-72 animate-fade-in overflow-hidden">
                          <div className="px-4 py-2.5 border-b border-divider">
                            <p className="text-txt-primary text-sm font-semibold">Отложенные сообщения</p>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {scheduledForChat.map((m) => (
                              <div key={m.id} onPointerDown={(e) => e.stopPropagation()} className="flex items-start gap-3 px-4 py-2.5 hover:bg-bg-hover transition-colors">
                                <div className="flex-1 min-w-0">
                                  <p className="text-txt-meta text-xs mb-0.5">{new Date(m.scheduledFor).toLocaleString()}</p>
                                  <p className="text-txt-primary text-sm truncate">{m.text || '📎 Медиа'}</p>
                                </div>
                                <button
                                  onClick={() => removeScheduled(m.id)}
                                  className="text-txt-meta hover:text-red-400 transition-colors shrink-0 mt-1 text-sm"
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => setShowSendLaterPopup((v) => !v)}
                      className="relative w-8 h-8 flex items-center justify-center rounded-full hover:bg-bg-hover text-accent transition-colors"
                      title="Отложенные сообщения"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center leading-none">
                        {scheduledForChat.length}
                      </span>
                    </button>
                  </div>
                )}
                {/* Voice / Video-circle button: tap to toggle mode, hold to record */}
                <RecordButton chatId={chatId} />
              </div>
            )}
          </div>
        </>
    </div>
  )
}
